import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Box, Chip, CircularProgress, Dialog, IconButton, Typography } from '@mui/material'

import { enqueueSnackbar } from 'notistack'
import { lazy, Suspense, useState } from 'react'

import { ERROR_COLORS, GRAY_COLORS } from 'asterX-UI'
import { EyeIcon } from 'asterX-UI/Icons/EyeIcon'

import { useContractStore } from 'context/ContractStore'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import ActionModal from '../../asterX-UI/Modals/ActionModal'

const UserRevertModal = lazy(() => import('components/RoleModals/UserRevertModal/UserRevertModal'))

export const FileAccordion = ({
  data = [],
  guaranteeDocs = false,
  readonly = false,
  updateGuaranteeDocs = () => {}
}) => {
  const {
    contractStore: { loanApplicationData },
    contractStoreFunc: { setLoanApplicationData, setManagerWorking }
  } = useContractStore()

  const [openEdit, setOpenEdit] = useState(null)
  const [confirmDlg, setConfirmDlg] = useState({ open: false, fileId: null, category: '', name: '' })
  const [deleting, setDeleting] = useState(false)

  const closeDeleteConfirm = () => {
    if (deleting) {
      return
    }

    setConfirmDlg({ open: false, fileId: null, category: '', name: '' })
  }

  const closeEditModal = () => {
    setManagerWorking(false)
    setOpenEdit(null)
  }

  const openFile = async doc => {
    try {
      const response = await CreditFlow.getFile(doc.id)
      const contentType = response.headers['content-type'] || 'application/octet-stream'
      const blob = new Blob([response.data], { type: contentType })
      const url = URL.createObjectURL(blob)

      window.open(url, 'blank')
      setTimeout(() => URL.revokeObjectURL(url), 100)

      /* const a = document.createElement('a')
      a.href = url
      a.download = doc.originalName || 'file'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 100) // revoke after short delay (safer) */
    } catch (error) {
      console.error(error)
    }

    try {
      const response = await CreditFlow.getApplicationById(loanApplicationData?.id)
      setLoanApplicationData(response.data)
    } catch (error) {
      console.error(error)
    }
  }

  const deleteFile = async id => {
    if (!id) {
      enqueueSnackbar(`Нет данных для обработки!`, { variant: 'info' })
      return
    }
    const res = await CreditFlow.deleteFile(id)
    if (!res?.data) {
      enqueueSnackbar(`${res?.status}`, { variant: 'error' })
      return
    }
    const response = await CreditFlow.getApplicationById(loanApplicationData?.id)
    if (!guaranteeDocs) {
      setLoanApplicationData(response.data)
    } else {
      const updatedCollateralFiles = response?.data?.collateralFiles || []
      updateGuaranteeDocs(updatedCollateralFiles)
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDlg.fileId) return
    try {
      setDeleting(true)
      await deleteFile(confirmDlg.fileId)
      closeDeleteConfirm()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Suspense
        fallback={
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        }
      >
        {openEdit && !readonly && <UserRevertModal openModal={openEdit} closeModal={closeEditModal} />}
      </Suspense>

      <Box
        sx={{
          border: `1px solid ${GRAY_COLORS[200]}`,
          borderRadius: '12px',
          boxShadow: '0 1px 2px 0 rgba(10, 13, 18, 0.05)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {data.map((item, i) => {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: i === 0 ? 'none' : '1px solid #E9EAEB',
                padding: '16px 24px'
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#181D27' }}>{item.originalName}.pdf</Typography>

              <Box sx={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: '28px', alignItems: 'center' }}>
                  <IconButton onClick={() => openFile(item)}>
                    <EyeIcon />
                  </IconButton>
                </Box>

                <Chip
                  label={item.isRead ? 'Проверено' : 'Ожидает'}
                  variant="outlined"
                  sx={{
                    minWidth: '90px',
                    justifyContent: 'center',
                    fontWeight: 500,
                    fontSize: '12px',
                    border: '1px solid',
                    color: item.isRead ? '#067647' : '#B54708',
                    backgroundColor: item.isRead ? '#ECFDF3' : '#FFFAEB',
                    borderColor: item.isRead ? '#ABEFC6' : '#FEDF89'
                  }}
                />
              </Box>
            </Box>
          )
        })}
      </Box>

      <ActionModal
        open={confirmDlg.open}
        onClose={closeDeleteConfirm}
        onCancel={closeDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title={
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: '3px solid #ff2f6d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              order: -1
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20, color: '#ff2f6d' }} />
          </Box>
        }
        labelCancel="Отмена"
        labelConfirm={deleting ? 'Удаляю…' : 'Удалить'}
        color="secondary"
        cancelBtnColor={'error'}
        maxWidth="xs"
        disableConfirm={deleting}
      >
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Удаление файла</Typography>
        </Box>

        <Typography
          variant="body2"
          lang="ru"
          sx={{
            color: GRAY_COLORS[700],
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            WebkitHyphens: 'auto',
            msHyphens: 'auto'
          }}
        >
          {confirmDlg.name ? (
            <>
              Действительно удалить файл в категории <b>«{confirmDlg.category}»</b> с названием «{confirmDlg.name}»?
              <br />
              <b style={{ color: (ERROR_COLORS && ERROR_COLORS[600]) || '#d32f2f' }}>Это действие необратимо.</b>
            </>
          ) : (
            'Действительно удалить выбранный файл? Это действие необратимо.'
          )}
        </Typography>
      </ActionModal>
    </>
  )
}
